import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import axiosConfig from '../../../../../redux/Project/axiosConfig';
import { Label } from '../../../../../components/ui/label';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';

export type AuditProjectOption = { projectId: number; projectName: string };

interface AuditProjectFilterProps {
  value: string;
  onChange: (projectId: string) => void;
  className?: string;
}

export const AuditProjectFilter: React.FC<AuditProjectFilterProps> = ({
  value,
  onChange,
  className,
}) => {
  const [projects, setProjects] = useState<AuditProjectOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axiosConfig.get('project/getAllProjects');
        if (cancelled) return;
        if (res.status === 200) {
          const raw = get(res, ['data', 'data'], []);
          const list = Array.isArray(raw) ? raw : raw?.projects ?? [];
          setProjects(
            list
              .map((p: { projectId?: string | number; projectName?: string }) => ({
                projectId: Number(p.projectId),
                projectName: p.projectName ?? `Project ${p.projectId}`,
              }))
              .filter((p: AuditProjectOption) => Number.isFinite(p.projectId))
              .sort((a: AuditProjectOption, b: AuditProjectOption) =>
                a.projectName.localeCompare(b.projectName)
              )
          );
        }
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () => [
      { value: '__all__', label: 'All projects' },
      ...projects.map((p) => ({
        value: String(p.projectId),
        label: p.projectName,
      })),
    ],
    [projects]
  );

  return (
    <div className={className}>
      <Label className="text-[11px] text-heritage-grey">Project</Label>
      <PipeSelector
        value={value}
        options={options}
        onChange={onChange}
        placeholder={loading ? 'Loading projects…' : 'All projects'}
        searchable
        searchPlaceholder="Search projects…"
        emptyMessage="No projects match your search"
        triggerWidth="w-full"
        className="w-full"
      />
    </div>
  );
};
